<template>
    <div>
        <template class="system-font">
            <div class="table-responsive text-nowrap mt-2">
                <table class="table table-striped table-borderless table-hover table-sm">
                    <thead class="thead-dark">
                    <tr>
                        <th class="text-center">
                            <input class="select-table" type="checkbox" v-model="allSelected" />
                        </th>
                        <th @click="$emit('sort', 'company')" :class="thClass('company')">Company</th>
                        <th @click="$emit('sort', 'customer')" :class="thClass('customer')">Customer</th>
                        <th @click="$emit('sort', 'policy')" :class="thClass('policy')">Policy #</th>
                        <th @click="$emit('sort', 'status')" :class="thClass('status')">Status</th>
                        <th @click="$emit('sort', 'type')" :class="thClass('type')">Type</th>
                        <th @click="$emit('sort', 'effdate')" :class="thClass('effdate')">Effective</th>
                        <th @click="$emit('sort', 'expdate')" :class="thClass('expdate')">Expiration</th>
                        <th @click="$emit('sort', 'commissionable')" :class="thClass('premium')">Premium</th>
                        <th @click="$emit('sort', 'commission')" :class="thClass('commission')">Commission</th>
                        <th @click="$emit('sort', 'adjusted')" :class="thClass('adjusted')">Adjusted</th>
                        <th @click="$emit('sort', 'diff')" :class="thClass('diff')">Diff</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-if="allSelected && selectedCount < totalCount">
                        <td class="text-center" colspan="12">
                            <template v-if="!massUpdate"><strong>{{ selectedCount }}</strong> details selected
                                <button class="btn btn-sm py-0 btn-link" @click="massUpdate = true">Click Here to select all <strong>{{ totalCount }}</strong></button>
                            </template>
                            <template v-else >All <strong>{{ totalCount }}</strong> details selected
                                <button class="btn btn-sm py-0 btn-link" @click="allSelected = false">Clear selection</button>
                            </template>
                        </td>
                    </tr>
                    <tr v-for="detail in value" :key="detail.id" :class="{dirty: detail.dirty}">
                        <td class="text-center">
                            <router-link v-if="detail.newDetail" :to="{name: 'commissions.reconcile.file', params: {id: detail.newDetail.commissionFilingID}}">
                                <svg-icon icon="view" title="View" class="am-glyphicon-medium am-glyphicon-blue"></svg-icon>
                            </router-link>
                            <input v-else-if="detail.status !== 3" type="checkbox" v-model="detail.selected" />
                        </td>
                        <td class="truncate"><old-link :to="'/#/admin/companies/View?id='+detail.company.id" target="_blank" tabindex="-1">{{ detail.company.name }}</old-link></td>
                        <td class="truncate">
                            <old-link :to="'customersDisplay.php?id='+detail.customer.id" target="_blank" tabindex="-1" v-if="detail.customer">{{ detail.customer.name }}</old-link>
                            <small class="text-danger" v-else>Deleted</small>
                        </td>
                        <td>{{ detail.policy.policy }}</td>
                        <td>{{ detail.policy.status }}</td>
                        <td class="truncate">
                            <old-link :to="'/#/admin/companies_products/View?id='+detail.product.id" target="_blank" tabindex="-1">{{ detail.product.name }}</old-link></td>
                        <td>{{ detail.policy.effdate }}</td>
                        <td>{{ detail.policy.expdate }}</td>
                        <td>
                            <div class="d-flex justify-content-between"><span>$</span><span>{{ detail.commissionable | number }}</span></div>
                        </td>
                        <td>
                            <div class="d-flex justify-content-between"><span>$</span><span>{{ detail.commission | number }}</span></div>
                        </td>
                        <td class="hl-dirty am-adjusted-td" :class="sourceClass(detail.source)">
                            <numeric class="text-right pull-right" :precision="2" :read-only="!editable" :minus="true" v-model="detail.amount" @input="checkDirty(detail)" @keyup.enter.native="$emit('adjust')" tabindex="2"></numeric>
                        </td>
                        <td class="hl-dirty">
                            <div class="d-flex justify-content-between"><span>$</span><span>{{ detail.amount - detail.commission | number }}</span></div>
                        </td>
                    </tr>
                    </tbody>
                    <tfoot>
                    <tr>
                        <td class="border-0" colspan="5"></td>
                        <td class="text-center" colspan="3"><strong>TOTAL</strong></td>
                        <td>
                            <div class="d-flex justify-content-between"><span class="pr-1">$</span><span>{{ sum('commissionable') | number }}</span></div>
                        </td>
                        <td>
                            <div class="d-flex justify-content-between"><span class="pr-1">$</span><span>{{ sum('commission') | number }}</span></div>
                        </td>
                        <td class="hl-dirty">
                            <div class="d-flex justify-content-between"><span class="pr-1">$</span><span>{{ sum('amount') | number }}</span></div>
                        </td>
                        <td class="hl-dirty">
                            <div class="d-flex justify-content-between"><span class="pr-1">$</span><span>{{ diffTotal | number }}</span></div>
                        </td>
                    </tr>
                    </tfoot>
                </table>
            </div>
        </template>
    </div>
</template>

<script>
import Numeric from 'vue-numeric'
import sumBy from 'lodash.sumby'
import OldLink from '@/components/OldLink'

export default {
  name: 'CompanyTable',

  components: {
    OldLink,
    Numeric,
  },

  props: {
    value: {
      type: Array,
      required: true
    },

    totalCount: {
      type: Number,
      default: 0
    },

    editable: {
      required: true
    },

    sort: {
      type: Object,
      required: true
    }
  },

  data () {
    return {
      sources: {
        1: 'tag tag-red',
        2: 'tag tag-red',
        4: 'tag tag-green',
        5: 'tag tag-blue'
      },
      massUpdate: false
    }
  },

  computed: {
    selectedCount () {
      return this.value.filter(i => i.selected).length
    },

    hasSelection () {
      return this.selectedCount > 0
    },

    dirty () {
      return this.value.filter(i => i.dirty)
    },

    hasDirty () {
      return this.dirty.length > 0
    },

    diffTotal () {
      return sumBy(this.value, d => d.amount - d.commission)
    },

    allSelected: {
      get () {
        return this.selectedCount === this.value.length
      },
      set (val) {
        this.value
          .filter(d => d.status !== 3 && !d.newDetail)
          .map(d => { d.selected = val })
      }
    }
  },

  methods: {
    sum (field) {
      return this.value.map(a => a[field]).reduce((a, b) => a + b, 0)
    },

    sourceClass (i) {
      return this.sources[i] || ''
    },

    checkDirty (detail) {
      detail.dirty = detail.amount !== detail.adjusted
    },

    thClass (name) {
      return {
        sorting: true,
        sorting_asc: this.sort.sort === name && !this.sort.reverse,
        sorting_desc: this.sort.sort === name && this.sort.reverse
      }
    }
  },

  watch: {
    allSelected (val) {
      if (!val) {
        this.massUpdate = false
      }
    },

    massUpdate (val) {
      this.$emit('bulkAction', val)
    }
  }
}
</script>

<style lang="sass" scoped>
  table.table > thead > tr > th.sorting
    position: relative
    padding-right: 1.5em
    cursor: pointer
    &::before, &::after
      position: absolute
      bottom: 0
      display: block
      opacity: 0.4
      padding-bottom: inherit
      font-size: inherit
    &::before
      right: 0.25em
      content: '\2191'
    &::after
      right: 0.75em
      content: '\2193'
    &.sorting_desc::before
      opacity: 1
    &.sorting_asc::after
      opacity: 1

  tr.dirty
    td.hl-dirty, .hl-dirty input
      color: #900
      font-weight: bold

  input[type="tel"]
    width: 100%

  .system-font
    font-family: Arial, Helvetica, sans-serif
    font-size: 11px
  .am-adjusted-td span
    float: right
    text-align: right
</style>
